import DataTable from "examples/Tables/CustomDataTable";
import QuotationService from "services/QuotationService";
import React, { useEffect, useState, useMemo } from "react";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ThinkingSpinner from "components/ThinkingSpinner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/authContext";
import { QUOTATION_STATUS_LIST } from "statusConstants";
import { format, parseISO } from "date-fns";
import { MenuItem, TextField } from "@mui/material";

const DEFAULT_QUOTATION_STATUS = "O";
const filterFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: 36,
    backgroundColor: "#fff",
  },
  "& .MuiInputBase-input": {
    paddingTop: "8px",
    paddingBottom: "8px",
  },
};
const filterActionButtonSx = {
  minHeight: 36,
  minWidth: 132,
  whiteSpace: "nowrap",
  px: 2,
};

export default function Data() {
  const [quotations, setQuotations] = useState([]);
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    customerName: "",
    dateFrom: "",
    dateTo: "",
    status: DEFAULT_QUOTATION_STATUS,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    customerName: "",
    dateFrom: "",
    dateTo: "",
    status: DEFAULT_QUOTATION_STATUS,
  });

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {       
        const quotationsResponse = await QuotationService.GetQuotations(null, null, token, page, pageSize, appliedFilters, { signal: controller.signal })
        const fetchedQuotations = quotationsResponse.data.data || [];

        setQuotations(fetchedQuotations);
        setTotalPages(quotationsResponse.data.totalPages);
        setTotalCount(quotationsResponse.data.totalItems);
      }
      catch (error)
      {
        if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
          console.error("Erro ao buscar dados:", error);
          setQuotations([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      }
      finally {
        setIsApplyingFilters(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [token, page, pageSize, appliedFilters]);

  const handlePageChange = (pageIndex) => {
    if (pageIndex < 1 || pageIndex > totalPages) return;
    setPage(pageIndex);
  };

  const handlePageSizeChange = (newPageSize) => {
    if (Number(newPageSize) !== Number(pageSize)){
      setPageSize(Number(newPageSize))
      setPage(1);
    }
  };

  const handleFilterChange = (field, value) => {
    setDraftFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const applyFilters = () => {
    setIsApplyingFilters(true);
    setPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    const resetFilters = {
      customerName: "",
      dateFrom: "",
      dateTo: "",
      status: DEFAULT_QUOTATION_STATUS,
    };
    setIsApplyingFilters(true);
    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setPage(1);
  };

  const handleFilterKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const handleExport = (startDate, endDate) => {
    // Aqui você insere a lógica real de exportação
    // Pode chamar API, gerar arquivo, etc.
    // Retorne o caminho onde foi salvo para exibir no Snackbar
    const path = `/exports/quotations_${startDate}_a_${endDate}.xlsx`;
    return path;
  };

  const rows = useMemo(() => 
    quotations.map((quot) => ({
      Id: (
        <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
          {quot.ExternalId}
        </MDTypography>
      ),
      CompanyName: (
        <MDTypography component="a" variant="caption" color="text" fontWeight="medium">
          {quot.Tennant.CompanyName}
        </MDTypography>
      ),
      Lancamento: (
        <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
          {format(parseISO(quot.DocDate), "dd/MM/yyyy")}
        </MDTypography>
      ),
      Status: (
        <MDTypography component="a" variant="caption" color="text" fontWeight="medium">
          {QUOTATION_STATUS_LIST.find((status) => quot.Status === status.Id)?.Description || quot.Status || "-"}
        </MDTypography>
      ),
      details: (
        <MDBox display="flex" gap={2}>
          <MDButton
            size="medium"
            variant="contained"
            color="warning"
            onClick={() => navigate(`/ofertas/${quot.Guid}`)}
          >
            Detalhes
          </MDButton>
        </MDBox>
      ),
    }))
  , [quotations]);

  return (
    <>
      <MDBox
        mb={3}
        p={1.5}
        borderRadius="lg"
        shadow="xs"
        bgColor="white"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(180px, 1.2fr) repeat(3, minmax(130px, 0.8fr)) auto auto",
          },
          gap: 1.5,
          alignItems: "end",
        }}
      >
        <MDBox>
          <TextField
            fullWidth
            size="small"
            label="Cliente solicitante"
            value={draftFilters.customerName}
            onChange={(event) => handleFilterChange("customerName", event.target.value)}
            onKeyDown={handleFilterKeyDown}
            sx={filterFieldSx}
          />
        </MDBox>
        <MDBox>
          <TextField
            fullWidth
            size="small"
            label="Data de"
            type="date"
            value={draftFilters.dateFrom}
            onChange={(event) => handleFilterChange("dateFrom", event.target.value)}
            InputLabelProps={{ shrink: true }}
            onKeyDown={handleFilterKeyDown}
            sx={filterFieldSx}
          />
        </MDBox>
        <MDBox>
          <TextField
            fullWidth
            size="small"
            label="Data até"
            type="date"
            value={draftFilters.dateTo}
            onChange={(event) => handleFilterChange("dateTo", event.target.value)}
            InputLabelProps={{ shrink: true }}
            onKeyDown={handleFilterKeyDown}
            sx={filterFieldSx}
          />
        </MDBox>
        <MDBox>
          <TextField
            fullWidth
            size="small"
            select
            label="Status"
            value={draftFilters.status}
            onChange={(event) => handleFilterChange("status", event.target.value)}
            sx={filterFieldSx}
          >
            <MenuItem value="">Todos</MenuItem>
            {QUOTATION_STATUS_LIST.map((statusOption) => (
              <MenuItem key={statusOption.Id} value={statusOption.Id}>
                {statusOption.Description}
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDButton
          variant="gradient"
          color="info"
          onClick={applyFilters}
          disabled={isApplyingFilters}
          size="small"
          sx={filterActionButtonSx}
        >
          <MDBox display="inline-flex" alignItems="center" gap={1}>
            {isApplyingFilters && <ThinkingSpinner color="currentColor" />}
            {isApplyingFilters ? "Aplicando" : "Aplicar filtros"}
          </MDBox>
        </MDButton>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={handleClearFilters}
          disabled={isApplyingFilters}
          size="small"
          sx={filterActionButtonSx}
        >
          Limpar filtros
        </MDButton>
      </MDBox>
      <DataTable
        table={{
          columns: [
            { Header: "Oferta", accessor: "Id", width: "26%", align: "left" },
            { Header: "Nome da Empresa", accessor: "CompanyName", width: "30%", align: "left" },
            { Header: "Data de Criação", accessor: "Lancamento", width: "14%", align: "left" },
            { Header: "Status da Oferta", accessor: "Status", width: "20%", align: "left" },
            { Header: "", accessor: "details", width: "10%", align: "left" },
          ],
          rows: rows,
        }}
        isSorted={true}
        entriesPerPage={true}
        showTotalEntries={true}
        noEndBorder
        handleExport={handleExport}
        customTitle="Exportar Ofertas"
        isAdmin={user?.Role?.Description?.toLowerCase() === 'admin'}
        totalCount={totalCount}
        pageCount={totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={page}
        pageSize={pageSize}
      />
    </>
  );
}
